import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ScanFace } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
export function AnalyzingStep() {
  const {
    t
  } = useLanguage();
  const [messageIndex, setMessageIndex] = useState(0);
  const messages = [t('faceAnalysis.analyzing.step1'), t('faceAnalysis.analyzing.step2'), t('faceAnalysis.analyzing.step3')];
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [messages.length]);
  return <div className="flex flex-col items-center justify-center py-24 min-h-[60vh]" data-id="element-182">
      <div className="relative w-44 h-44 mb-10" data-id="element-183">
        {/* Pulsing rings */}
        {[0, 0.6, 1.2].map((delay, i) => <motion.div key={i} animate={{
        scale: [1, 1.6, 1],
        opacity: [0.4, 0, 0.4]
      }} transition={{
        duration: 2.4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay
      }} className="absolute inset-0 rounded-full border border-brand-copper/60" data-id="element-184" />)}

        {/* Rotating gradient arc */}
        <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 2.2,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-2" data-id="element-185">
          <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-186">
            <defs data-id="element-187">
              <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-188">
                <stop offset="0%" stopColor="#c79268" stopOpacity="0" data-id="element-189" />
                <stop offset="100%" stopColor="#c79268" stopOpacity="1" data-id="element-190" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="44" fill="none" stroke="url(#arcGrad)" strokeWidth="3" strokeLinecap="round" strokeDasharray="140 280" data-id="element-191" />
          </svg>
        </motion.div>

        {/* Counter-rotating thin arc */}
        <motion.div animate={{
        rotate: -360
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-5" data-id="element-192">
          <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-193">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#e6b89c" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="20 260" data-id="element-194" />
          </svg>
        </motion.div>

        {/* Orbiting dots */}
        {[0, 120, 240].map((angle, i) => <motion.div key={i} initial={{
        rotate: angle
      }} animate={{
        rotate: angle + 360
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-0" data-id="element-195">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-brand-copper shadow-[0_0_8px_#c79268]" data-id="element-196" />
          </motion.div>)}

        {/* Center icon */}
        <div className="absolute inset-8 flex items-center justify-center bg-white rounded-full shadow-xl shadow-brand-copper/20 z-10" data-id="element-197">
          <motion.div animate={{
          scale: [1, 1.08, 1]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-198">
            <ScanFace className="w-12 h-12 text-brand-copper" data-id="element-199" />
          </motion.div>

          {/* Scanning line */}
          <motion.div animate={{
          top: ['18%', '82%', '18%']
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute left-5 right-5 h-0.5 bg-brand-copper/80 shadow-[0_0_8px_#c79268] z-20 rounded-full" data-id="element-200" />
        </div>
      </div>

      <h3 className="text-2xl sm:text-3xl font-light text-gray-900 mb-3" data-id="element-201">
        {t('faceAnalysis.analyzing.title')}
      </h3>

      {/* Cycling status message */}
      <div className="h-7 overflow-hidden mb-7" data-id="element-202">
        <motion.p key={messageIndex} initial={{
        y: 24,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -24,
        opacity: 0
      }} className="text-gray-500 font-medium" data-id="element-203">
          {messages[messageIndex]}
        </motion.p>
      </div>

      {/* Indeterminate progress bar (loops, never completes) */}
      <div className="w-64 max-w-[80vw] h-1.5 bg-brand-copper/10 rounded-full overflow-hidden" data-id="element-204">
        <motion.div animate={{
        x: ['-100%', '250%']
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="h-full w-1/3 bg-gradient-to-r from-transparent via-brand-copper to-transparent rounded-full" data-id="element-205" />
      </div>
    </div>;
}